import type { SnowplowDataAdapters, SnowplowUserSnapshot } from '@nextmar/snowplow-sdk';
import { getCapturedSnowplowUserId } from './capturedUserId';
import type { User } from '@/api/types/public';
import { hasSensorsInitialized } from '@/reporting/reliability/identityGate';
import { getSensorsIdentity, readLiveSensorsAnonymousId } from '@/reporting/utils/sensorsIdentity';
import { ZL_USERINFO } from '@/utils/constants';
import { getPlatformLanguage } from '@/utils/locale-cookie';

/** Z 登录 token 的既有 localStorage key。 */
const ZL_TOKEN_STORAGE_KEY = 'zl-token';

/** SDK 允许接收的 Z 归因字段；expires 只供 SDK 判断有效期。 */
const ZEELOOL_ATTRIBUTION_KEYS = [
  'expires',
  'pub_zl_utm_source',
  'pub_zl_utm_medium',
  'pub_zl_utm_campaign',
  'pub_zl_utm_campaignid',
  'pub_zl_referrer',
  'pub_zl_page',
  'pub_landing_page',
] as const;

/** 带 Z 归因全局值的最小 Window 形状。 */
interface ZeeloolAttributionWindow {
  latestChannelAttribute?: Readonly<Record<string, unknown>>;
}

/** 把业务身份值规范为 SDK 使用的非空字符串。 */
function normalizeIdentityValue(value: unknown): string | undefined {
  if (
    value === '' ||
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === 'undefined'
  ) {
    return undefined;
  }

  return String(value);
}

/** 按旧公共属性合同把异常会员等级映射为 9999。 */
function resolveMemberLevel(value: unknown): string {
  const normalizedValue = normalizeIdentityValue(value);
  if (!normalizedValue) return '9999';

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= -1 ? normalizedValue : '9999';
}

/** 安全读取并解析 Z 用户与 token；任一 storage/JSON/getter 异常都返回空快照。 */
function getUserSnapshot(): SnowplowUserSnapshot {
  try {
    const storage = globalThis.localStorage;
    const rawUser = storage?.getItem(ZL_USERINFO);
    const parsedUser = JSON.parse(rawUser ?? '{}') as User | null;
    if (!parsedUser || typeof parsedUser !== 'object' || Array.isArray(parsedUser)) {
      return {};
    }

    const uuid = normalizeIdentityValue(parsedUser.uuid);
    const customerId = normalizeIdentityValue(parsedUser.id);
    const email = normalizeIdentityValue(parsedUser.email);
    const phone = normalizeIdentityValue(parsedUser.telephone);

    return {
      ...(uuid ? { uuid } : {}),
      ...(customerId ? { customerId } : {}),
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      isLoggedIn: !!storage?.getItem(ZL_TOKEN_STORAGE_KEY),
      memberLevel: resolveMemberLevel(parsedUser.member_level),
      isPaid: !!parsedUser.last_pay_time,
    };
  } catch {
    return {};
  }
}

/** 安全复制当前窗口中的批准归因字段，不保留业务全局对象引用。 */
function getAttributionSnapshot(): Readonly<Record<string, unknown>> {
  try {
    const currentWindow = globalThis.window as unknown as ZeeloolAttributionWindow | undefined;
    const source = currentWindow?.latestChannelAttribute;
    if (!source || typeof source !== 'object' || Array.isArray(source)) return {};

    const snapshot: Record<string, unknown> = {};
    for (const key of ZEELOOL_ATTRIBUTION_KEYS) {
      const value = source[key];
      if (value !== undefined) snapshot[key] = value;
    }
    return snapshot;
  } catch {
    return {};
  }
}

/** 创建 Z 业务数据同步适配器；这是 Task 10 唯一读取相关 storage/global 的 SDK 层。 */
export function createZeeloolSnowplowDataAdapters(): SnowplowDataAdapters {
  return {
    /** 仅冻结已经成功显式同步的客户ID，配置前资料不自动成为根uid。 */
    getTrackerUserId: getCapturedSnowplowUserId,
    /** 兼容旧SDK resolver，live ID缺失时禁止cookie兜底。 */
    isSensorsInitialized: () => hasSensorsInitialized() && !!readLiveSensorsAnonymousId(),
    /** anonymousId仅来自本窗口live SDK；既有distinctId仅在该身份存在时读取。 */
    getIdentity() {
      const anonymousId = readLiveSensorsAnonymousId();
      if (!anonymousId) return {};
      try {
        return { ...getSensorsIdentity(), anonymousId };
      } catch {
        return { anonymousId };
      }
    },
    /** 安全读取用户、会员、支付和登录状态。 */
    getUser() {
      return getUserSnapshot();
    },
    /** 安全读取当前归因及过期元数据。 */
    getAttribution() {
      return getAttributionSnapshot();
    },
    /** 安全读取当前平台语言。 */
    getPlatformLanguage() {
      try {
        const language = getPlatformLanguage();
        return typeof language === 'string' ? language : undefined;
      } catch {
        return undefined;
      }
    },
  };
}
